import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { CButton, CContainer } from "@coreui/react-pro";
import { FailureAlert } from "../components/common/FailureAlert";
import { FailureContext, type Failure, type FailureContextValue } from "./useFailure";

// Enough for two lines of message plus the reference. Reserved at the end of the page so the fixed
// bar never ends up sitting on top of the footer once you scroll to the bottom.
const RESERVED_SPACE = "7.5rem";

interface ProviderProps {
  children: ReactNode;
}

/**
 * The anchored failure bar: one slot, full content width, pinned to the bottom of the viewport while
 * a page action's failure stands.
 *
 * A dialog does not use this — its failure belongs inside the dialog, above its own buttons, or it
 * would render behind the backdrop where nobody can read it.
 */
export function FailureProvider({ children }: ProviderProps) {
  const [raised, setRaised] = useState<(Failure & { path: string }) | null>(null);
  const { pathname } = useLocation();

  const clearFailure = useCallback(() => setRaised(null), []);

  // A failure belongs to the route that raised it, so it is filtered during render rather than
  // cleared in an effect: navigating away simply stops matching. A member should never land on the
  // next screen still reading about the last one.
  const failure = raised?.path === pathname ? raised : null;

  const value = useMemo<FailureContextValue>(
    () => ({
      showFailure: (message, reference) =>
        setRaised({ message, reference, path: window.location.pathname }),
      clearFailure,
    }),
    [clearFailure]
  );

  return (
    <FailureContext.Provider value={value}>
      {children}
      {failure && (
        <>
          <div aria-hidden style={{ height: RESERVED_SPACE }} />
          <div
            role="alert"
            // left tracks the sidebar: CoreUI publishes its occupied width on the wrapper, and it
            // resolves to 0 on mobile where the sidebar overlays rather than occupies.
            style={{
              position: "fixed",
              left: "var(--cui-sidebar-occupy-start, 0px)",
              right: 0,
              bottom: 0,
              zIndex: 1030,
            }}
          >
            <CContainer lg className="px-4 pb-3">
              <FailureAlert
                message={failure.message}
                reference={failure.reference}
                className="mb-0 shadow"
              >
                <CButton
                  color="danger"
                  variant="outline"
                  size="sm"
                  onClick={clearFailure}
                  className="mt-3"
                >
                  Dismiss
                </CButton>
              </FailureAlert>
            </CContainer>
          </div>
        </>
      )}
    </FailureContext.Provider>
  );
}
